import React, { useState, useEffect, useRef, useCallback } from 'react';
import { API_CONFIG } from '../../config/api';
import { Navigate, useLocation } from 'react-router-dom';
import { useSimpleAuth } from '../../contexts/SimpleAuthContext';
import LoadingSpinner from './LoadingSpinner';
import {
  clearAdminSessionStorage,
  clearAuthSessionStorage,
  notifyAuthSessionChanged,
  isJwtExpired,
} from '../../utils/authUtils';

const rejectAdminSession = (reason) => {
  clearAdminSessionStorage();
  clearAuthSessionStorage();
  notifyAuthSessionChanged(reason);
};

const AdminProtectedRoute = ({ children }) => {
  const { isLoading, isAuthenticated } = useSimpleAuth();
  const location = useLocation();
  const [checking, setChecking] = useState(true);
  const [adminToken, setAdminToken] = useState(null);
  const hasCheckedRef = useRef(false); // เพิ่ม ref เพื่อป้องกัน re-check
  const [forceLogout, setForceLogout] = useState(false);

  // ฟังก์ชัน reset state เมื่อ logout
  const resetAuthState = useCallback(() => {
    console.log('🚪 AdminProtectedRoute: Resetting auth state');
    setAdminToken(null);
    setChecking(false);
    setForceLogout(true);
    hasCheckedRef.current = false;
  }, []);

  // Listen for authLogout event
  useEffect(() => {
    const handleLogout = () => {
      console.log('🚪 AdminProtectedRoute: authLogout event received');
      resetAuthState();
    };

    window.addEventListener('authLogout', handleLogout);
    
    // Check if tokens exist - if not, reset state
    const checkTokens = () => {
      const token = localStorage.getItem('adminToken');
      const authToken = localStorage.getItem('authToken');
      
      if (!token && !authToken && adminToken) {
        console.log('🚪 AdminProtectedRoute: Tokens cleared, resetting state');
        resetAuthState();
      }
    };
    
    // Check periodically for token removal
    const intervalId = setInterval(checkTokens, 500);

    return () => {
      window.removeEventListener('authLogout', handleLogout);
      clearInterval(intervalId);
    };
  }, [adminToken, resetAuthState]);

  // Reset forceLogout and recheck when isAuthenticated changes
  useEffect(() => {
    if (isAuthenticated === false) {
      console.log('🚪 AdminProtectedRoute: isAuthenticated is false, forcing logout');
      resetAuthState();
    }
  }, [isAuthenticated, resetAuthState]);

  useEffect(() => {
    // ถ้า forceLogout เป็น true ไม่ต้อง check
    if (forceLogout) {
      console.log('🚪 AdminProtectedRoute: Force logout active, skipping check');
      return;
    }
    
    // ป้องกัน re-check ถ้าเคย check แล้วและยังอยู่ในหน้าเดิม
    if (hasCheckedRef.current && location.pathname.startsWith('/admin')) {
      return;
    }
    
    const checkAdminAuth = async () => {
      try {
        // ตรวจสอบ admin token ใน localStorage
        let token = localStorage.getItem('adminToken');
        let adminUserData = localStorage.getItem('adminUserData');
        
        // Fallback: ถ้าไม่มี adminToken แต่มี authToken และ role เป็น admin
        if (!token || !adminUserData) {
          const authToken = localStorage.getItem('authToken');
          const authUser = localStorage.getItem('authUser') || localStorage.getItem('userData');
          
          if (authToken && authUser) {
            try {
              const userData = JSON.parse(authUser);
              if (userData.role === 'admin' || userData.role === 'Admin') {
                console.log('🔍 Found admin user in authUser, using authToken as adminToken');
                token = authToken;
                adminUserData = authUser;
                
                // Store as adminToken for future use
                localStorage.setItem('adminToken', authToken);
                localStorage.setItem('adminUserData', authUser);
              }
            } catch (parseError) {
              console.error('Error parsing authUser:', parseError);
            }
          }
        }
        
        console.log('🔍 AdminProtectedRoute checking credentials:', { 
          hasToken: !!token, 
          hasUserData: !!adminUserData,
          pathname: location.pathname 
        });
        
        if (!token || !adminUserData) {
          console.log('❌ No admin credentials found');
          setChecking(false);
          hasCheckedRef.current = true; // Mark as checked to prevent re-check
          return;
        }

        if (isJwtExpired(token)) {
          console.log('❌ Admin token expired');
          rejectAdminSession('admin_token_expired');
          setChecking(false);
          return;
        }

        // ตรวจสอบกับ server
        try {
          console.log('🔍 Calling admin/verify API...');
          
          // เพิ่ม timeout สำหรับ API call
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 วินาที
          
          // Use correct API endpoint: /api/admin/auth/verify
          const apiUrl = API_CONFIG.BASE_URL || (process.env.NODE_ENV === 'production' 
            ? 'https://www.ptthaiplus.com/ptthaiplus_app/api'
            : 'http://localhost:3001/api');
          
          console.log('🔍 Calling admin verify API:', `${apiUrl}/admin/auth/verify`);
          
          const response = await fetch(`${apiUrl}/admin/auth/verify`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            signal: controller.signal
          });
          
          clearTimeout(timeoutId);

          console.log('📡 API Response status:', response.status);

          if (!response.ok) {
            if (response.status === 401) {
              console.log('❌ Admin token invalid or expired (401)');
              rejectAdminSession('admin_verify_401');
              setChecking(false);
              return;
            }
            throw new Error(`Admin token verification failed: ${response.status}`);
          }

          const data = await response.json();
          console.log('✅ Admin authentication verified:', data);
          setAdminToken(token);
          setChecking(false);
          hasCheckedRef.current = true; // Mark as checked
        } catch (error) {
          console.log('❌ Admin token verification failed:', error.message);
          
          // ถ้าเป็น network error หรือ timeout ให้ใช้ client-side validation แทน
          if (error.message.includes('Failed to fetch') || 
              error.message.includes('NetworkError') || 
              error.name === 'AbortError') {
            console.log('🌐 Network error or timeout, using client-side validation');
            setAdminToken(token);
            setChecking(false);
            hasCheckedRef.current = true; // Mark as checked
            return;
          }
          
          rejectAdminSession('admin_verify_failed');
          setChecking(false);
          return;
        }
      } catch (error) {
        console.error('Error checking admin auth:', error);
        setChecking(false);
      }
    };

    checkAdminAuth();
  }, [forceLogout]); // เพิ่ม forceLogout dependency

  // ถ้า forceLogout เป็น true ให้ redirect ทันที
  if (forceLogout) {
    console.log('🚪 AdminProtectedRoute: Force logout - redirecting to signin');
    return (
      <Navigate 
        to="/signin" 
        state={{ 
          from: location.pathname,
          message: 'คุณได้ออกจากระบบแล้ว' 
        }} 
        replace 
      />
    );
  }

  // แสดง loading ขณะตรวจสอบ
  if (isLoading || checking) {
    return <LoadingSpinner fullScreen text="กำลังตรวจสอบสิทธิ์แอดมิน..." />;
  }

  // ถ้าไม่มี admin token ให้ redirect ไปหน้า login
  if (!adminToken) {
    console.log('🚫 No admin token - redirecting to signin');
    console.log('🔍 Available tokens:', {
      adminToken: localStorage.getItem('adminToken') ? 'exists' : 'not found',
      authToken: localStorage.getItem('authToken') ? 'exists' : 'not found',
      authUser: localStorage.getItem('authUser') ? 'exists' : 'not found',
      userData: localStorage.getItem('userData') ? 'exists' : 'not found'
    });
    
    // Check if user is already logged in as admin but adminToken is missing
    // แต่ไม่ reload เพื่อป้องกัน infinite loop
    const authUser = localStorage.getItem('authUser') || localStorage.getItem('userData');
    const authToken = localStorage.getItem('authToken');
    
    if (authUser && authToken) {
      try {
        const userData = JSON.parse(authUser);
        if (userData.role === 'admin' || userData.role === 'Admin') {
          console.log('🔍 Found admin user in authUser, storing as adminToken');
          localStorage.setItem('adminToken', authToken);
          localStorage.setItem('adminUserData', authUser);
          // ใช้ setState แทน reload เพื่อป้องกัน infinite loop
          setAdminToken(authToken);
          hasCheckedRef.current = true;
          return <LoadingSpinner fullScreen text="กำลังตรวจสอบสิทธิ์แอดมิน..." />;
        }
      } catch (parseError) {
        console.error('Error parsing authUser:', parseError);
      }
    }
    
    return (
      <Navigate 
        to="/signin" 
        state={{ 
          from: location.pathname,
          message: 'กรุณาเข้าสู่ระบบด้วยบัญชีแอดมิน' 
        }} 
        replace 
      />
    );
  }

  // ถ้าผ่านการตรวจสอบแล้ว ให้แสดงหน้าแอดมิน
  return children;
};

export default AdminProtectedRoute;
